import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { User } from '../types';
import { adminAPI } from '../services/api/adminAPI';

interface AuthContextType {
	user: User | null;
	isAuthenticated: boolean;
	login: (email: string, password: string) => Promise<void>;
	logout: () => void;
	loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

interface AuthProviderProps {
	children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);

	const checkAuth = async () => {
		const token = localStorage.getItem('adminToken');
		if (!token) {
			setLoading(false);
			return;
		}

		try {
			const currentUser = adminAPI.getCurrentUser();
			if (currentUser) {
				setUser(currentUser);
			}
		} catch (error) {
			console.error('Auth check failed:', error);
			logout();
		} finally {
			setLoading(false);
		}
	};

	const login = async (email: string, password: string): Promise<void> => {
		console.log('[AUTH] login function called');
		const result = await adminAPI.login(email, password);
		console.log('[AUTH] login API completed, setting user');

		// Map role string to UserRole enum
		const user = {
			...result.user,
			role: result.user.role as any, // Backend sends string, will be properly typed
		};

		setUser(user);
		console.log('[AUTH] user set, login complete', {
			role: user.role,
			clientId: user.clientId,
			clientName: user.clientName,
		});
	};

	const logout = () => {
		adminAPI.logout();
		setUser(null);
	};

	useEffect(() => {
		checkAuth();
	}, []);

	const value: AuthContextType = {
		user,
		isAuthenticated: !!user,
		login,
		logout,
		loading,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
